// Распознавание структуры СТО во вставленном фрагменте: плоские элементы (dom.ts) -> узлы схемы редактора.
//   заголовки: стиль/тег/уровень структуры/«ручной» номер «1.2 Текст» -> heading нужного уровня без номера;
//   «Введение», «Заключение», «Список использованных источников»… -> structHeading; «Приложение А» -> appendix;
//   списки Word (mso-list) и «ручные» маркеры «-», «а)», «1)» -> stoList нужного вида с вложенностью;
//   «Рисунок 3 – …» -> подпись рисунка; «Таблица 2 – …» -> название таблицы; «Листинг 1 – …» -> листинг;
//   формула в таблице-вёрстке «формула | (5)» -> formula с номером; «где …» -> экспликация формулы;
//   «Примечание – …» -> note (после таблицы - tableNote); «Пример – …» -> example;
//   список источников -> библиотека источников, «[3, с. 5]» -> cite; «(рисунок 3)», «в таблице 2», «формуле (5)»,
//   «приложении А», «разделе 2.1» -> перекрёстные ссылки xref на вставленные объекты (номера становятся живыми);
//   титульный лист, оглавление, лист для замечаний - не вставляются (их строит writer), задание/реферат - в данные работы.
import type { JSONContent as J } from '@tiptap/core';
import { newId } from '../../core/schema/util';
import type { Item, DomResult } from './dom';
import { trimInline, inlineText, textNode } from './dom';
import type { SourceApp } from './prep';

export type LinkMode = 'text' | 'keep' | 'sources';
export type PasteMode = 'structure' | 'basic';

export interface StructOptions {
  mode: PasteMode;
  links: LinkMode;
  app: SourceApp;
  /** уровень заголовка перед местом вставки (0 - нет) - для относительных уровней веб-страниц */
  ctxLevel: number;
  /** «слово в нижнем регистре» -> написание в тексте (для регистра заголовков, набранных капсом) */
  forms: Map<string, string>;
  /** дата обращения для веб-источников (ДД.ММ.ГГГГ) */
  today: string;
}

export interface NewSource { id: string; kind: string; text?: string; title?: string; url?: string; accessed?: string }

export interface PasteStats {
  headings: number; struct: number; paragraphs: number; lists: number; figures: number; tables: number;
  formulas: number; listings: number; footnotes: number; notes: number; xrefs: number; cites: number;
  sources: number; appendices: number; missingImages: number; emptyRemoved: number; numbersRemoved: number;
  recased: number; links: number; lostInTables: number; titleSkipped: boolean; tocSkipped: boolean;
  imagesFromTables?: number; nestedTables?: number; commentsRemoved?: number;
  listsFixed: number; titleMoved?: boolean; oleEquations?: number; noCaption?: number;
}

export interface StructResult {
  blocks: J[];
  appendices: J[];
  sources: NewSource[];
  meta: Record<string, any>;
  stats: PasteStats;
  /** узлы рисунков, для которых надо загрузить картинку (attrs.__img = адрес) */
  figures: J[];
}

// ------------------------------------------------------------------------------------------------ шаблоны
const NUM = '(?:[А-ЯA-Z]\\.)?\\d{1,3}(?:\\.\\d{1,3}){0,3}';
const FIG_CAP = new RegExp('^\\s*(?:Рисунок|Рис\\.?)[\\s\\u00a0]*(' + NUM + ')(\\s*[.:–—-]?\\s*)(.*)$', 's');
const TBL_CAP = new RegExp('^\\s*(?:Таблица|Табл\\.?)[\\s\\u00a0]*(' + NUM + ')(\\s*[.:–—-]?\\s*)(.*)$', 's');
const TBL_CONT = new RegExp('^\\s*(?:Продолжение|Окончание)\\s+таблицы[\\s\\u00a0]*(' + NUM + ')\\s*\\.?\\s*$', 'i');
const LST_CAP = new RegExp('^\\s*Листинг[\\s\\u00a0]*(' + NUM + ')(\\s*[.:–—-]?\\s*)(.*)$', 's');
const HEAD_NUM = /^\s*(?:(?:Глава|Раздел|Часть)\s+)?((?:[А-ЯA-Z]\.)?\d{1,2}(?:\.\d{1,2}){0,3})\.?[\s\u00a0]+(?=\S)/;
const EQ_NUM_ONLY = /^\s*\(\s*((?:[А-ЯA-Z]\.)?\d{1,3}(?:\.\d{1,3})?)\s*\)\s*[.,;]?\s*$/;
const DASH_MARK = /^[-–—•·▪■□◦○●❖➢✓√§oо•‣⁃▪●\uf0b7\uf0a7\uf0d8\uf076\uf0fc\uf0a8\uf02d]$/;
const MANUAL_MARK = /^[\s\u00a0]*(?:([-–—•·▪◦●])|([а-яё])\)|(\d{1,2})\))[\s\u00a0]+/;
const MANUAL_NUM_DOT = /^[\s\u00a0]*(\d{1,2})\.[\s\u00a0]+(?=[А-ЯЁA-Z«"(])/;
const TITLE_HINT = /министерство|федеральное государственное|университет|филиал|выполнил|обучающ|проверил|руководитель|кафедр|институт/i;

const STRUCT_TITLES: Record<string, string> = {
  'введение': 'intro', 'заключение': 'conclusion', 'выводы': 'conclusions', 'вывод': 'conclusions',
  'общие выводы': 'conclusions',
  'список использованных источников': 'sources', 'список использованной литературы': 'sources',
  'список литературы': 'sources', 'список источников': 'sources', 'литература': 'sources',
  'библиографический список': 'sources', 'использованные источники': 'sources', 'источники': 'sources',
  // 3.5.1 СТО 2026: все варианты - один элемент «Определения, обозначения и сокращения»
  'термины и определения': 'defs', 'определения': 'defs',
  'перечень сокращений и обозначений': 'defs', 'перечень сокращений': 'defs', 'список сокращений': 'defs',
  'обозначения и сокращения': 'defs', 'сокращения': 'defs', 'перечень сокращений, условных обозначений, символов, единиц и терминов': 'defs',
  'сокращения и обозначения': 'defs', 'перечень условных обозначений': 'defs',
  'определения, обозначения и сокращения': 'defs', 'нормативные ссылки': 'normrefs',
};
const SPECIAL_TITLES: [RegExp, string][] = [
  [/^(оглавление|содержание)$/, 'toc'],
  [/^лист для замечаний$/, 'remarks'],
  [/^задание( на .*)?$/, 'zadanie'],
  [/^реферат$/, 'referat'],
  [/^аннотация$/, 'annotation'],
];
const APP_HEAD = /^\s*приложени[ея]\s+([А-ЯЁA-Z])(?![А-Яа-яЁёA-Za-z])\s*\.?\s*(?:\(\s*(обязательное|рекомендуемое|справочное)\s*\))?\s*[.:–—-]?\s*(.*)$/is;
const APP_STATUS = /^\s*\(\s*(обязательное|рекомендуемое|справочное)\s*\)\s*$/i;

export const normKey = (s: string) => s.toLowerCase().replace(/ё/g, 'е').replace(/[\s\u00a0]+/g, ' ').replace(/[.:]+$/, '').trim();

// ------------------------------------------------------------------------------------------------ строчные помощники
/** Убрать первые n символов (как в inlineText) из строчного содержимого. */
export function stripPrefix(inl: J[], n: number): J[] {
  const out = inl.map((x) => ({ ...x }));
  let i = 0;
  while (n > 0 && i < out.length) {
    const nd = out[i];
    if (nd.type === 'text') {
      const len = (nd.text || '').length;
      if (len <= n) { n -= len; out.splice(i, 1); continue; }
      nd.text = nd.text!.slice(n);
      n = 0;
      break;
    }
    if (nd.type === 'mathInline' || nd.type === 'hardBreak') { n -= 1; out.splice(i, 1); continue; }
    i++;
  }
  trimInline(out);
  return out;
}

/** Убрать последние символы-знаки (точку в конце заголовка/подписи). */
function stripTrailing(inl: J[], re: RegExp): J[] {
  const out = inl.map((x) => ({ ...x }));
  const last = out[out.length - 1];
  if (last && last.type === 'text') {
    last.text = last.text!.replace(re, '');
    if (!last.text) out.pop();
  }
  return out;
}

/** Только текст и формулы (заголовки и подписи оформляет writer: без полужирного, курсива, ссылок). */
function plainMarks(inl: J[], keep = ['superscript', 'subscript']): J[] {
  const out: J[] = [];
  for (const n of inl) {
    if (n.type === 'text') {
      const marks = (n.marks || []).filter((m) => keep.includes(m.type));
      const t: J = { type: 'text', text: n.text };
      if (marks.length) t.marks = marks;
      out.push(t);
    } else if (n.type !== 'hardBreak' || out.length) out.push(n.type === 'hardBreak' ? { type: 'text', text: ' ' } : n);
  }
  trimInline(out);
  return out;
}

const WORD_RE = /[A-Za-zА-Яа-яЁё]+/g;
const SHORT_WORDS = new Set(('и в во на для по от из с со к ко о об обо при без над под про или их его ее её как что это все ' +
  'вид тип ток шаг код ряд вес два три ход бит сеть сети оба одна один не ни да нет а но же ли бы до за так там тут где год лет').split(' '));

/** Заголовок капсом -> «с прописной, далее строчные»; аббревиатуры - как в тексте (как sto_lib.sentence_case). */
export function sentenceCase(text: string, forms: Map<string, string>): string {
  let first = true;
  return text.replace(WORD_RE, (w) => {
    const key = w.toLowerCase();
    let nw: string;
    const f = forms.get(key);
    if (f && f.length === w.length) nw = f;
    else if (w === w.toUpperCase() && /^[A-Z]+$/.test(w) && w.length <= 6) nw = w;
    else if (w === w.toUpperCase() && w.length >= 2 && w.length <= 3 && !SHORT_WORDS.has(key)) nw = w;
    else if (w !== w.toUpperCase() && w !== w.toLowerCase() && w.slice(1) !== w.slice(1).toLowerCase()) nw = w;
    else nw = key;
    if (first) { nw = nw[0].toUpperCase() + nw.slice(1); first = false; }
    return nw;
  });
}

const isCaps = (t: string) => {
  const letters = t.replace(/[^A-Za-zА-Яа-яЁё]/g, '');
  return letters.length >= 4 && letters === letters.toUpperCase() && /[А-ЯЁ]{3}/.test(letters);
};

/** Заголовок/подпись: капс -> «с прописной»; отдельные слова капсом или со сбитым регистром («РАБОТа») - строчными
 *  (кроме аббревиатур, известных по тексту); первая буква - прописная (4.2.5). Длина текста не меняется. */
export function fixCase(text: string, forms: Map<string, string>): string {
  if (isCaps(text)) return sentenceCase(text, forms);
  let out = text.replace(WORD_RE, (w) => {
    if (w.length >= 4 && /[А-ЯЁ]/.test(w) && w === w.toUpperCase()) {
      const f = forms.get(w.toLowerCase());
      return f && f === f.toUpperCase() ? w : w.toLowerCase();
    }
    if (w.length >= 4 && /[А-ЯЁ]/.test(w) && w !== w.toUpperCase() && w !== w.toLowerCase() &&
        !(w[0] === w[0].toUpperCase() && w.slice(1) === w.slice(1).toLowerCase()) && forms.get(w.toLowerCase()) !== w) return w.toLowerCase();
    return w;
  });
  const m = out.match(/[A-Za-zА-Яа-яЁё]/);
  if (m && m.index !== undefined && out[m.index] !== out[m.index].toUpperCase() && !/^[a-z]+[A-Z]/.test(out.slice(m.index, m.index + 8))) {
    out = out.slice(0, m.index) + out[m.index].toUpperCase() + out.slice(m.index + 1);
  }
  return out.length === text.length ? out : text;
}

function recaseInline(inl: J[], forms: Map<string, string>): J[] {
  const full = inlineText(inl);
  const fixed = fixCase(full, forms);
  if (fixed === full) return inl;
  let pos = 0;
  return inl.map((n) => {
    if (n.type === 'text') {
      const len = n.text!.length;
      const t = { ...n, text: fixed.slice(pos, pos + len) };
      pos += len;
      return t;
    }
    if (n.type === 'mathInline' || n.type === 'hardBreak') pos += 1;
    return n;
  });
}

/** Формы написания слов по основному тексту (не в начале предложения). */
export function collectForms(texts: string[], into = new Map<string, Map<string, number>>()): Map<string, Map<string, number>> {
  for (const t of texts) {
    const letters = t.replace(/[^A-Za-zА-Яа-яЁё]/g, '');
    if (letters.length < 8 || letters.replace(/[^A-ZА-ЯЁ]/g, '').length > letters.length / 2) continue;
    let m: RegExpExecArray | null;
    const re = new RegExp(WORD_RE.source, 'g');
    while ((m = re.exec(t))) {
      const pre = t.slice(0, m.index).replace(/[\s\u00a0«"(]+$/, '');
      if (!pre || /[.!?:\n]$/.test(pre)) continue;
      const key = m[0].toLowerCase();
      const c = into.get(key) || new Map<string, number>();
      c.set(m[0], (c.get(m[0]) || 0) + 1);
      into.set(key, c);
    }
  }
  return into;
}
export function bestForms(cnt: Map<string, Map<string, number>>): Map<string, string> {
  const out = new Map<string, string>();
  for (const [k, c] of cnt) out.set(k, [...c.entries()].sort((a, b) => b[1] - a[1])[0][0]);
  return out;
}

const para = (inl: J[]): J => (inl.length ? { type: 'paragraph', content: inl } : { type: 'paragraph' });
const cap = (inl: J[]): J => (inl.length ? { type: 'caption', content: inl } : { type: 'caption' });
const onlyMath = (inl: J[]) => inl.length > 0 && inl.every((n) => n.type === 'mathInline' ||
  (n.type === 'text' && /^[\s\u00a0.,;:]*$/.test(n.text || '')));

function latexOf(n: J): string {
  const m = (n as any).__math;
  return String((m && m.latex) || n.attrs?.latex || '');
}

// ------------------------------------------------------------------------------------------------ сборщик
interface ListEntry { level: number; kind: string; inl: J[] }

export class StructBuilder {
  private out: J[] = [];
  private apps: J[] = [];
  private curApp: J | null = null;
  private maps = { fig: new Map<string, string>(), tbl: new Map<string, string>(), eq: new Map<string, string>(),
    lst: new Map<string, string>(), app: new Map<string, string>(), sec: new Map<string, string>() };
  private srcByNum = new Map<number, string>();
  private sources: NewSource[] = [];
  private wikiSrc = new Map<string, string>();
  private meta: Record<string, any> = {};
  private special: string | null = null;
  private specialBlocks: J[] = [];
  private pendingTbl: { inl: J[]; num: string } | null = null;
  /** картинки из ячеек текущей таблицы - встанут рисунками после неё */
  private cellImgs: any[] = [];
  private pendingLst: { inl: J[]; num: string } | null = null;
  private tblHeader = new Map<string, number>();
  private figures: J[] = [];
  private headingShift = 0;
  stats: PasteStats = { headings: 0, struct: 0, paragraphs: 0, lists: 0, figures: 0, tables: 0, formulas: 0, listings: 0,
    footnotes: 0, notes: 0, xrefs: 0, cites: 0, sources: 0, appendices: 0, missingImages: 0, emptyRemoved: 0,
    numbersRemoved: 0, recased: 0, links: 0, lostInTables: 0, titleSkipped: false, tocSkipped: false, listsFixed: 0 };

  constructor(private dom: DomResult, private o: StructOptions) {
    this.stats.footnotes = dom.footnotes;
    this.stats.oleEquations = dom.oleEquations;
    this.stats.emptyRemoved = dom.dropped.empty;
    this.stats.links = dom.links.length;
  }

  private get S() { return this.o.mode === 'structure'; }
  private get target(): J[] { return this.curApp ? this.curApp.content! : this.out; }
  private last(): J | undefined { const t = this.target; return t[t.length - 1]; }
  private emit(n: J) {
    if (this.special && this.special !== 'sources') {
      if (['zadanie', 'referat', 'annotation'].includes(this.special) && ['paragraph', 'stoList'].includes(n.type!)) this.specialBlocks.push(n);
      return;
    }
    if (!n.attrs?.id && ['paragraph', 'heading', 'structHeading', 'stoList', 'figure', 'stoTable', 'formula', 'listing',
      'note', 'example', 'appendix'].includes(n.type!)) n.attrs = { ...(n.attrs || {}), id: newId() };
    this.target.push(n);
  }

  build(): StructResult {
    let items = this.dom.items;
    if (this.S) items = this.skipTitle(items);
    this.headingShift = this.computeShift(items);
    for (let i = 0; i < items.length; i++) i = this.item(items, i);
    this.closeSpecial();
    this.dropEmptyServiceHeadings();
    // рисунки и таблицы без названия (4.5.2, 4.9.4) - автору ввести
    const noCap = (arr: J[]) => arr.reduce((k, n) => k + ((n.type === 'figure' || n.type === 'stoTable') &&
      !(n.content?.[0]?.type === 'caption' && n.content[0].content?.length) ? 1 : 0) + noCap(n.type === 'appendix' ? n.content || [] : []), 0);
    if (this.S) this.stats.noCaption = noCap(this.out) + noCap(this.apps);
    if (this.S) this.postRefs();
    this.stats.sources = this.sources.length;
    return { blocks: this.out, appendices: this.apps, sources: this.sources, meta: this.meta, stats: this.stats, figures: this.figures };
  }

  /** «Примечания», «Ссылки», «См. также» веб-страниц: содержимое ушло в источники/сноски - пустой заголовок убрать. */
  private dropEmptyServiceHeadings() {
    const re = /^(примечания|сноски|ссылки|см\. также|внешние ссылки|литература|источники|комментарии)$/;
    for (const arr of [this.out, ...this.apps.map((a) => a.content!)]) {
      for (let k = arr.length - 1; k >= 0; k--) {
        const n = arr[k];
        if (n.type !== 'heading' || !re.test(normKey(inlineText(n.content || [])))) continue;
        const next = arr[k + 1];
        if (!next || next.type === 'heading' || next.type === 'structHeading') { arr.splice(k, 1); this.stats.headings--; }
      }
    }
  }

  // --- титульный лист: не вставляется; из него - данные работы
  private skipTitle(items: Item[]): Item[] {
    let first = items.findIndex((it) => it.k === 'p' && (!!it.hLevel || !!STRUCT_TITLES[normKey(it.text)] ||
      SPECIAL_TITLES.some(([re]) => re.test(normKey(it.text)))));
    if (first < 3) return items;
    const head = items.slice(0, first);
    const texts = head.flatMap((it) => (it.k === 'p' ? [it.text] : it.k === 'table' ? [it.el.textContent || ''] : []));
    const hits = texts.filter((t) => TITLE_HINT.test(t)).length;
    if (hits < 2) return items;
    this.stats.titleSkipped = true;
    const all = texts.join('\n').replace(/[\s\u00a0]+/g, ' ');
    const q = all.match(/(?:на тему|тема)[:\s]*[«"]([^»"]{3,300})[»"]/i) || all.match(/[«"]([^»"]{8,300})[»"]/);
    const disc = all.match(/по дисциплине[:\s]*[«"]([^»"]+)[»"]/i);
    const num = all.match(/работ[аы]\s*№\s*(\d+)/i);
    const vr = all.match(/вариант\s*№?\s*(\d+)/i);
    if (disc) this.meta.discipline = disc[1].trim();
    if (q && (!disc || q[1] !== disc[1])) this.meta.title = q[1].trim();
    if (num) this.meta.number = num[1];
    if (vr) this.meta.variant = vr[1];
    return items.slice(first);
  }

  private computeShift(items: Item[]): number {
    if (!['web', 'markdown', 'gdocs', 'text'].includes(this.o.app)) return 0;
    const lv = items.filter((it) => it.k === 'p' && it.hLevel && !HEAD_NUM.test(it.text)).map((it) => (it as any).hLevel as number);
    if (!lv.length) return 0;
    const min = Math.min(...lv);
    return Math.min(3, this.o.ctxLevel + 1) - min;
  }

  // --- разбор одного элемента; возвращает индекс последнего потреблённого
  private item(items: Item[], i: number): number {
    const it = items[i];
    switch (it.k) {
      case 'p': return this.paragraph(items, i);
      case 'img': this.image(it.img.src, it.img, it.img.caption); return i;
      case 'table': return this.table(items, i);
      case 'code': this.listing(it.code, it.lang); return i;
      case 'formula': {
        const n: J = { type: 'formula', attrs: { latex: it.math.latex || '', numbered: false, where: [] } };
        (n as any).__math = it.math;
        this.stats.formulas++;
        this.emit(n);
        return i;
      }
      case 'list': {
        if (this.special === 'sources') { this.listToSources(it.node); return i; }
        const node = normalizeListJson(it.node);
        if (this.S && node.attrs?.kind === 'digit') { node.attrs = { ...node.attrs, kind: 'letter' }; this.stats.listsFixed++; }
        this.stats.lists++;
        this.emit(node);
        return i;
      }
      default: return i;
    }
  }

  private closeSpecial() {
    const sp = this.special;
    if (sp && ['zadanie', 'referat', 'annotation'].includes(sp) && this.specialBlocks.length) {
      this.meta[sp] = { type: 'doc', content: this.specialBlocks.map((b) => ({ ...b, attrs: { ...(b.attrs || {}), id: newId() } })) };
    }
    this.special = null;
    this.specialBlocks = [];
  }

  // --- абзацы
  private paragraph(items: Item[], i: number): number {
    const it = items[i] as Extract<Item, { k: 'p' }>;
    const text = it.text.trim();
    const key = normKey(text);
    if (it.toc) { this.stats.tocSkipped = true; return i; }
    // стиль Word «Название» в начале фрагмента - тема работы (она на титуле), а не раздел
    if (this.S && it.docTitle && !this.out.length && !this.apps.length && text.length < 300) {
      this.meta.title = text.replace(/[.\s]+$/, '');
      this.stats.titleMoved = true;
      return i;
    }
    // заголовок?
    let level = it.hLevel;
    if (this.S && !level && !it.list && this.looksPseudoHeading(it, items[i + 1])) level = HEAD_NUM.exec(text)![1].split('.').length;
    if (this.S && !level && (STRUCT_TITLES[key] || SPECIAL_TITLES.some(([re]) => re.test(key))) && text.length < 60 &&
        (it.bold || it.align === 'center' || isCaps(text))) level = 1;
    if (this.S && level && APP_HEAD.test(text) && text.length < 300) return this.appendix(items, i);
    if (this.S && !level && APP_HEAD.test(text) && text.length < 120 && (it.align === 'center' || it.bold || isCaps(text)) &&
        (APP_STATUS.test(nextText(items, i)) || /\((обязательное|рекомендуемое|справочное)\)/i.test(text))) return this.appendix(items, i);
    if (level) return this.heading(it, level, i);
    // особые части
    if (this.special === 'toc' || this.special === 'remarks') return i;
    if (this.special === 'sources' && this.S) { this.textToSource(it); return i; }
    if (!this.S) return this.plainParagraph(items, i);
    // подписи
    if (!it.list) {
      const fm = FIG_CAP.exec(text);
      if (fm && text.length < 400 && !(!/[.:–—-]/.test(fm[2]) && /^[а-яё]/.test(fm[3]))) return this.figCaption(items, i, fm);
      const tc = TBL_CONT.exec(text);
      if (tc) { this.pendingTbl = { inl: [], num: '+' + tc[1] }; return i; }
      const tm = TBL_CAP.exec(text);
      if (tm && text.length < 400 && !(!/[.:–—-]/.test(tm[2]) && /^[а-яё]/.test(tm[3]))) {
        this.pendingTbl = { inl: this.captionInl(it.inl, text.length - tm[3].length), num: tm[1] };
        return i;
      }
      const lm = LST_CAP.exec(text);
      if (lm && text.length < 300 && !it.mono) {
        this.pendingLst = { inl: this.captionInl(it.inl, text.length - lm[3].length), num: lm[1] };
        return i;
      }
    }
    // формула отдельной строкой (с номером «(5)» или без)
    if (!it.list && onlyMathWithNumber(it.inl)) return this.formulaPara(it, i);
    // экспликация «где …»
    const last = this.last();
    if (/^где[\s\u00a0:]/i.test(text) && last?.type === 'formula') return this.where(items, i, last);
    // примечания и примеры
    const nm = text.match(/^Примечани[ея][\s\u00a0]*[–—-][\s\u00a0]*/);
    if (nm) return this.note(it, nm[0].length, last, i);
    if (/^Примечания[\s\u00a0]*:?$/.test(text)) return this.notes(items, i, last);
    const em = text.match(/^Пример[\s\u00a0]*[–—:-][\s\u00a0]*/);
    if (em) {
      this.emit({ type: 'example', content: [para(stripPrefix(it.inl, em[0].length))] });
      this.stats.notes++;
      return i;
    }
    // код (моноширинные абзацы подряд) -> листинг
    if (it.mono && !it.list) {
      const lines: string[] = [];
      let j = i;
      while (j < items.length && items[j].k === 'p' && (items[j] as any).mono && !(items[j] as any).list) {
        lines.push(((items[j] as any).el?.textContent || (items[j] as any).text).replace(/\u00a0/g, ' ').replace(/\r?\n/g, ''));
        j++;
      }
      if (lines.length >= 2 || this.pendingLst || /[;{}()=]|^\s*(def|class|import|#include|for|if|print)\b/.test(lines[0])) {
        this.listing(lines.join('\n'), '');
        return j - 1;
      }
    }
    // перечисления
    const entry = this.listEntry(it);
    if (entry) return this.list(items, i);
    this.stats.paragraphs++;
    this.emit(para(it.inl));
    return i;
  }

  private plainParagraph(items: Item[], i: number): number {
    const it = items[i] as Extract<Item, { k: 'p' }>;
    if (it.list) return this.list(items, i);
    if (onlyMathWithNumber(it.inl) && it.inl.some((n) => n.type === 'mathInline' && (n as any).__math?.display)) return this.formulaPara(it, i);
    this.stats.paragraphs++;
    this.emit(para(it.inl));
    return i;
  }

  private looksPseudoHeading(it: Extract<Item, { k: 'p' }>, next?: Item): boolean {
    const t = it.text.trim();
    const m = HEAD_NUM.exec(t);
    if (!m || t.length > 160) return false;
    if (Number(m[1].split('.')[0].replace(/^[А-ЯA-Z]\./, '')) > 40 || m[1].split('.').length > 3) return false;
    const rest = t.slice(m[0].length);
    if (!/^[А-ЯЁA-Z«"]/.test(rest)) return false;
    if (/[.:;,]$/.test(t) && !/\.\.$/.test(t)) return false;
    if (it.bold || /заголов|heading/.test(it.styleName || '')) return true;
    // без полужирного: короткая строка, за которой идёт обычный текст
    return t.length < 90 && m[1].includes('.') && !!next && next.k === 'p' && next.text.length > t.length && !next.hLevel;
  }

  private heading(it: Extract<Item, { k: 'p' }>, level0: number, i: number): number {
    const text = it.text.trim();
    const key = normKey(text.replace(HEAD_NUM, ''));
    if (this.S) {
      const sp = SPECIAL_TITLES.find(([re]) => re.test(key));
      if (sp) {
        this.closeSpecial();
        this.special = sp[1];
        if (sp[1] === 'toc') this.stats.tocSkipped = true;
        if (sp[1] === 'zadanie') this.meta.zadanie_title = text.replace(/[.:]+$/, '');
        return i;
      }
      const kind = STRUCT_TITLES[key];
      if (kind) {
        this.closeSpecial();
        if (kind === 'sources') this.special = 'sources';
        this.curApp = null;
        this.emit({ type: 'structHeading', attrs: { kind } });
        this.stats.struct++;
        return i;
      }
    }
    if (this.special === 'sources' || this.special) this.closeSpecial();
    let inl = it.inl;
    let level = level0;
    if (this.S) {
      const m = HEAD_NUM.exec(text);
      if (m) {
        inl = stripPrefix(inl, m[0].length);
        level = m[1].replace(/^[А-ЯA-Z]\./, 'X.').split('.').length;
        this.stats.numbersRemoved++;
      } else level = level0 + this.headingShift;
      inl = plainMarks(stripTrailing(inl, /(?<!\.)\.$/));
      const re = recaseInline(inl, this.o.forms);
      if (re !== inl) { this.stats.recased++; inl = re; }
      if (m) this.pendingSec = m[1];
    }
    level = Math.max(this.curApp ? 2 : 1, Math.min(3, level));
    const node: J = { type: 'heading', attrs: { id: newId(), level }, content: inl.length ? inl : undefined };
    if (!inl.length) delete node.content;
    if (this.pendingSec) { this.maps.sec.set(this.pendingSec, node.attrs!.id); this.pendingSec = null; }
    this.stats.headings++;
    this.emit(node);
    return i;
  }
  private pendingSec: string | null = null;

  private appendix(items: Item[], i: number): number {
    this.closeSpecial();
    const it = items[i] as Extract<Item, { k: 'p' }>;
    const m = APP_HEAD.exec(it.text.trim())!;
    let status = (m[2] || '').toLowerCase();
    let titleInl = stripPrefix(it.inl, it.text.trim().length - m[3].length);
    let j = i;
    // «(обязательное)» и название - следующими абзацами
    if (!status && APP_STATUS.test(nextText(items, j))) { j = nextIndex(items, j); status = APP_STATUS.exec((items[j] as any).text)![1].toLowerCase(); }
    if (!titleInl.length) {
      const k = nextIndex(items, j);
      const nx = items[k];
      if (nx && nx.k === 'p' && nx.text.length < 200 && !nx.list && (nx.align === 'center' || nx.bold || nx.hLevel)) { titleInl = nx.inl; j = k; }
    }
    titleInl = recaseInline(plainMarks(stripTrailing(titleInl, /(?<!\.)\.$/)), this.o.forms);
    const node: J = { type: 'appendix', attrs: { id: newId(), letter: null, status: status || 'обязательное' },
      content: [titleInl.length ? { type: 'appendixTitle', content: titleInl } : { type: 'appendixTitle' }] };
    this.maps.app.set(m[1].toUpperCase(), node.attrs!.id);
    this.apps.push(node);
    this.curApp = node;
    this.stats.appendices++;
    return j;
  }

  // --- рисунки
  private image(src: string, img: { width?: number; height?: number; alt?: string } = {}, caption?: J[]) {
    const n: J = { type: 'figure', attrs: { id: newId(), src: '', width: null, alt: img.alt && !/^(image|рисунок|изображение)\d*$/i.test(img.alt) && img.alt.length < 200 ? img.alt : '' },
      content: [cap(caption ? plainMarks(caption) : [])] };
    (n as any).__img = src;
    this.figures.push(n);
    this.stats.figures++;
    this.emit(n);
    if (caption?.length && this.S) this.linkCaption(n, caption);
  }

  private linkCaption(fig: J, inl: J[]) {
    // подпись вида «Рисунок 2 – …» в <figcaption>
    const t = inlineText(inl);
    const m = FIG_CAP.exec(t);
    if (m) {
      fig.content = [cap(this.captionInl(inl, t.length - m[3].length))];
      this.maps.fig.set(m[1], fig.attrs!.id);
    }
  }

  private captionInl(inl: J[], prefix: number): J[] {
    let out = stripPrefix(inl, prefix);
    out = plainMarks(stripTrailing(out, /(?<!\.)\.$/));
    return recaseInline(out, this.o.forms);
  }

  private figCaption(items: Item[], i: number, m: RegExpExecArray): number {
    const it = items[i] as Extract<Item, { k: 'p' }>;
    const inl = this.captionInl(it.inl, it.text.trim().length - m[3].length);
    const last = this.last();
    let fig: J | null = null;
    if (last?.type === 'figure' && !(last.content?.[0]?.content?.length)) fig = last;
    let j = i;
    if (!fig) {
      const k = nextIndex(items, i);
      if (items[k]?.k === 'img') {                       // подпись над рисунком
        const img = (items[k] as any).img;
        this.image(img.src, img);
        fig = this.last()!;
        j = k;
      }
    }
    if (!fig) {                                          // картинки в буфере нет - место под неё
      this.image('', {});
      fig = this.last()!;
      this.stats.missingImages++;
    }
    fig.content = [cap(inl)];
    this.maps.fig.set(m[1], fig.attrs!.id);
    return j;
  }

  // --- таблицы
  private table(items: Item[], i: number): number {
    const tit = items[i] as Extract<Item, { k: 'table' }>;
    const el = tit.el;
    if (this.special === 'toc' || this.special === 'remarks') return i;
    const rows = directRows(el);
    const cells = rows.map((r) => directCells(r));
    const ncell = cells.reduce((a, r) => a + r.length, 0);
    const itemsOf = (c: Element) => tit.cells.get(c) || [];
    // 1) формула в таблице-вёрстке: «[пусто] | формула | (5)»
    if (rows.length <= 4 && cells.every((r) => r.length <= 4)) {
      const f = this.formulaTable(cells, itemsOf);
      if (f) return i;
    }
    // 2) рамка из одной ячейки или вёрстка с картинками - развернуть содержимое
    const all = cells.flat();
    const inner = all.flatMap((c) => itemsOf(c));
    const imgs = inner.filter((x) => x.k === 'img').length;
    const textLen = inner.filter((x) => x.k === 'p' && !FIG_CAP.test(x.text)).reduce((a, x: any) => a + x.text.length, 0);
    // вёрстка с картинками (картинки с подписями в ячейках) - это не таблица данных; таблица данных, где в одной
    // ячейке картинка (Google Документы, веб), остаётся таблицей: ячеек только с текстом больше, чем с картинками
    const imgCells = all.filter((c) => itemsOf(c).some((x) => x.k === 'img')).length;
    const textCells = all.filter((c) => !itemsOf(c).some((x) => x.k === 'img') &&
      itemsOf(c).some((x) => x.k === 'p' && x.text.trim() && !FIG_CAP.test(x.text))).length;
    const layout = imgs > 0 && textLen < 40 * imgs + 20 && textCells <= imgCells;
    if (ncell === 1 || layout || inner.every((x) => x.k === 'table')) {
      if (ncell === 1 && inner.length && inner.every((x) => x.k === 'p' && (x as any).mono)) {
        this.listing(inner.map((x: any) => (x.el?.textContent || x.text).replace(/\u00a0/g, ' ')).join('\n'), '');
        return i;
      }
      items.splice(i + 1, 0, ...inner);
      return i;
    }
    // 3) таблица с данными
    const node = this.dataTable(el, rows, cells, itemsOf);
    // «Продолжение таблицы N» - строки в ту же таблицу
    if (this.pendingTbl?.num.startsWith('+')) {
      const id = this.maps.tbl.get(this.pendingTbl.num.slice(1));
      const prev = id && this.findNode(id);
      this.pendingTbl = null;
      if (prev) {
        const hdr = this.tblHeader.get(id!) || 0;
        const newRows = node.content![1].content!;
        const tbl = prev.content!.find((c) => c.type === 'table')!;
        const same = newRows.slice(0, hdr).map(rowText).join('|') === tbl.content!.slice(0, hdr).map(rowText).join('|');
        tbl.content!.push(...newRows.slice(same ? hdr : 0));
        return i;
      }
    }
    if (this.pendingTbl) {
      node.content![0] = cap(this.pendingTbl.inl);
      if (this.pendingTbl.num) this.maps.tbl.set(this.pendingTbl.num, node.attrs!.id);
      this.pendingTbl = null;
    }
    this.stats.tables++;
    this.emit(node);
    // картинки из ячеек - отдельными рисунками сразу после таблицы (в ячейке таблицы по СТО рисунков не делают)
    const moved = this.cellImgs.splice(0);
    for (const img of moved) this.image(img.src, img, img.caption);
    if (moved.length) this.stats.imagesFromTables = (this.stats.imagesFromTables || 0) + moved.length;
    return i;
  }

  private findNode(id: string): J | null {
    let found: J | null = null;
    const walk = (n: J) => { if (found) return; if (n.attrs?.id === id) { found = n; return; } (n.content || []).forEach(walk); };
    [...this.out, ...this.apps].forEach(walk);
    return found;
  }

  private dataTable(el: HTMLTableElement, rows: HTMLTableRowElement[], cells: Element[][], itemsOf: (c: Element) => Item[]): J {
    let hdr = rows.filter((r) => r.parentElement?.tagName === 'THEAD').length;
    if (!hdr) { let k = 0; while (k < rows.length && cells[k].length && cells[k].every((c) => c.tagName === 'TH')) k++; hdr = k; }
    if (!hdr && rows.length >= 2) hdr = 1;
    // головка из нескольких строк: объединения по вертикали в первой строке
    if (hdr >= 1) {
      let span = 1;
      for (let r = 0; r < hdr; r++) for (const c of cells[r]) span = Math.max(span, r + (Number(c.getAttribute('rowspan')) || 1));
      hdr = Math.min(rows.length - (rows.length > 1 ? 1 : 0), Math.max(hdr, span));
    }
    const trs: J[] = [];
    rows.forEach((_r, ri) => {
      const tcs: J[] = [];
      for (const c of cells[ri]) {
        const cs = Math.max(1, Number(c.getAttribute('colspan')) || 1);
        const rs = Math.max(1, Number(c.getAttribute('rowspan')) || 1);
        const head = ri < hdr;
        const paras = this.cellParagraphs(itemsOf(c), head);
        const attrs: Record<string, any> = { colspan: cs, rowspan: rs, colwidth: null };
        // выравнивание граф из исходника не переносится: по СТО боковик - влево, значения - по центру (writer сам)
        if (this.o.mode === 'basic') {
          const al = (c.getAttribute('align') || cssAlign(c) || '').toLowerCase();
          if (!head && /^(left|center|right)$/.test(al)) attrs.align = al;
        }
        tcs.push({ type: head ? 'tableHeader' : 'tableCell', attrs, content: paras });
      }
      if (tcs.length) trs.push({ type: 'tableRow', content: tcs });
    });
    const id = newId();
    this.tblHeader.set(id, hdr);
    const widths = colWidths(rows, cells);
    return { type: 'stoTable', attrs: { id, widths }, content: [cap([]), { type: 'table', content: trs }] };
  }

  private cellParagraphs(items: Item[], head: boolean): J[] {
    const out: J[] = [];
    for (const x of items) {
      if (x.k === 'p') {
        let inl = x.inl;
        if (x.list) inl = [textNode(listMarkerText(x.list.marker) + ' ', {}), ...inl];
        if (head) inl = plainMarks(inl);
        out.push(para(inl));
      } else if (x.k === 'list') {
        for (const li of flattenList(x.node)) out.push(para(li));
      } else if (x.k === 'formula') {
        const n: J = { type: 'mathInline', attrs: { latex: x.math.latex || '' } };
        (n as any).__math = x.math;
        out.push(para([n]));
      } else if (x.k === 'code') {
        for (const line of x.code.split('\n')) out.push(para(line ? [textNode(line, { code: true })] : []));
      } else if (x.k === 'img') {
        this.cellImgs.push(x.img);
      } else if (x.k === 'table') {
        // вложенная таблица - строками «ячейка; ячейка» (иначе текст ячеек склеивается «в3в4»)
        for (const r of Array.from(x.el.querySelectorAll('tr'))) {
          const t = Array.from(r.children).map((c) => (c.textContent || '').replace(/\s+/g, ' ').trim()).filter(Boolean).join('; ');
          if (t) out.push(para([textNode(t, {})]));
        }
        this.stats.nestedTables = (this.stats.nestedTables || 0) + 1;
      }
    }
    return out.length ? out : [{ type: 'paragraph' }];
  }

  // --- формулы
  private formulaTable(cells: Element[][], itemsOf: (c: Element) => Item[]): boolean {
    const found: { math: J[]; num: string | null }[] = [];
    for (const row of cells) {
      const maths: J[][] = [];
      const nums: string[] = [];
      let other = false;
      for (const c of row) {
        for (const x of itemsOf(c)) {
          if (x.k === 'formula') { const n: J = { type: 'mathInline', attrs: { latex: x.math.latex || '' } }; (n as any).__math = x.math; maths.push([n]); continue; }
          if (x.k !== 'p') { if (x.k === 'img') other = true; continue; }
          const t = x.text.trim();
          if (!t && !x.inl.length) continue;
          const nm = t.match(/^\(\s*(?:[А-ЯA-Z]\.)?\d{1,3}(?:\.\d{1,3})?\s*\)(\s*\(\s*(?:[А-ЯA-Z]\.)?\d{1,3}(?:\.\d{1,3})?\s*\))*$/);
          if (nm) { for (const g of t.matchAll(/\(\s*((?:[А-ЯA-Z]\.)?\d{1,3}(?:\.\d{1,3})?)\s*\)/g)) nums.push(g[1]); continue; }
          if (x.inl.some((n) => n.type === 'mathInline') && onlyMath(x.inl)) { maths.push(x.inl.filter((n) => n.type === 'mathInline')); continue; }
          other = true;
        }
      }
      if (other || !maths.length) return false;
      maths.forEach((m, k) => found.push({ math: m, num: nums[k] ?? (maths.length === 1 && nums.length ? nums.join(',') : null) }));
    }
    if (!found.length) return false;
    for (const f of found) this.pushFormula(f.math, f.num);
    return true;
  }

  private pushFormula(maths: J[], num: string | null) {
    const n: J = { type: 'formula', attrs: { latex: maths.map(latexOf).join(' '), numbered: !!num, where: [] } };
    (n as any).__parts = maths;
    const mm = (maths[0] as any).__math;
    if (maths.length === 1 && mm) (n as any).__math = mm;
    this.stats.formulas++;
    this.emit(n);
    if (num) for (const k of num.split(',')) this.maps.eq.set(k, n.attrs!.id);
  }

  private formulaPara(it: Extract<Item, { k: 'p' }>, i: number): number {
    const maths = it.inl.filter((n) => n.type === 'mathInline');
    const t = it.inl.filter((n) => n.type === 'text').map((n) => n.text).join('');
    const nm = t.match(/\(\s*((?:[А-ЯA-Z]\.)?\d{1,3}(?:\.\d{1,3})?)\s*\)/);
    this.pushFormula(maths, nm ? nm[1] : null);
    return i;
  }

  private where(items: Item[], i: number, formula: J): number {
    const entries: { sym: string; desc: string }[] = [];
    let consumed = i - 1;
    for (let j = i; j < items.length; j++) {
      const x = items[j];
      if (x.k !== 'p' || x.list) break;
      let inl = x.inl;
      if (j === i) inl = stripPrefix(inl, (x.text.match(/^где[\s\u00a0:]*/i) || [''])[0].length);
      const e = splitWhere(inl);
      if (!e) break;
      entries.push(e);
      consumed = j;
      if (/\.\s*$/.test(x.text)) break;
    }
    if (!entries.length) return this.fallbackPara(items, i);
    formula.attrs = { ...formula.attrs, where: entries };
    return consumed;
  }

  private fallbackPara(items: Item[], i: number): number {
    this.stats.paragraphs++;
    this.emit(para((items[i] as any).inl));
    return i;
  }

  // --- примечания
  private note(it: Extract<Item, { k: 'p' }>, prefix: number, last: J | undefined, i: number): number {
    const p = para(stripPrefix(it.inl, prefix));
    if (last?.type === 'stoTable' && !last.content!.some((c) => c.type === 'tableNote')) last.content!.push({ type: 'tableNote', content: [p] });
    else this.emit({ type: 'note', content: [p] });
    this.stats.notes++;
    return i;
  }

  private notes(items: Item[], i: number, last?: J): number {
    const ps: J[] = [];
    let j = i + 1;
    while (j < items.length && items[j].k === 'p' && /^\d+[.)]?[\s\u00a0]+/.test((items[j] as any).text)) {
      const x = items[j] as any;
      ps.push(para(stripPrefix(x.inl, x.text.match(/^\d+[.)]?[\s\u00a0]+/)[0].length)));
      j++;
    }
    if (!ps.length) { this.emit(para((items[i] as any).inl)); return i; }
    if (last?.type === 'stoTable' && !last.content!.some((c) => c.type === 'tableNote')) last.content!.push({ type: 'tableNote', content: ps });
    else this.emit({ type: 'note', content: ps });
    this.stats.notes++;
    return j - 1;
  }

  // --- листинги
  private listing(code: string, lang: string) {
    const n: J = { type: 'listing', attrs: { id: newId(), lang, code: code.replace(/\t/g, '    ').replace(/\u00a0/g, ' ') },
      content: [cap(this.pendingLst ? this.pendingLst.inl : [])] };
    if (this.pendingLst?.num) this.maps.lst.set(this.pendingLst.num, n.attrs!.id);
    this.pendingLst = null;
    this.stats.listings++;
    this.emit(n);
  }

  // --- перечисления
  private listEntry(it: Item | undefined): ListEntry | null {
    if (!it || it.k !== 'p') return null;
    if (it.list) {
      const mk = it.list.marker;
      let kind = 'dash';
      if (/^[а-яёa-z][).]$/i.test(mk)) kind = 'letter';
      else if (/^\d+[.)]?$/.test(mk) || /^[IVXLC]+[.)]?$/.test(mk)) kind = 'digit';
      else if (/^\d+(\.\d+)+\.?$/.test(mk)) kind = 'digit';
      return { level: it.list.level, kind, inl: this.S ? stripManualMarker(it.inl) : it.inl };
    }
    if (!this.S) return null;
    const m = MANUAL_MARK.exec(it.text);
    if (m) return { level: -1, kind: m[1] ? 'dash' : m[2] ? 'letter' : 'digit', inl: stripPrefix(it.inl, m[0].length) };
    const d = MANUAL_NUM_DOT.exec(it.text);
    if (d && it.text.length < 1000) return { level: -1, kind: 'digit', inl: stripPrefix(it.inl, d[0].length) };
    return null;
  }

  private list(items: Item[], i: number): number {
    const entries: ListEntry[] = [];
    let j = i;
    for (; j < items.length; j++) {
      const e = this.listEntry(items[j]);
      if (!e) break;
      // «ручные» маркеры: уровень по смене вида (после «…:» - вложенный)
      if (e.level < 0) {
        if (!entries.length) e.level = 0;
        else {
          const prev = entries[entries.length - 1];
          const same = [...entries].reverse().find((x) => x.kind === e.kind && x.level <= prev.level);
          if (e.kind === prev.kind) e.level = prev.level;
          else if (same) e.level = same.level;
          else if (/:\s*$/.test(inlineText(prev.inl)) && prev.level < 2) e.level = prev.level + 1;
          else break;
        }
      }
      entries.push(e);
    }
    if (this.special === 'sources' && this.S) {
      for (const e of entries) this.addSource(inlineText(e.inl));
      return j - 1;
    }
    if (!entries.length) { this.emit(para((items[i] as any).inl)); return i; }
    if (this.S && fixTopDigits(entries)) this.stats.listsFixed++;
    this.emit(buildList(entries));
    this.stats.lists++;
    return j - 1;
  }

  // --- источники
  private addSource(text: string, num?: number) {
    const t = text.replace(/^\s*\[?\d{1,3}[\].)]?[\s\u00a0]+/, '').replace(/[\s\u00a0]+/g, ' ').trim();
    if (!t) return;
    const id = 's' + newId().slice(1);
    this.sources.push({ id, kind: /https?:\/\/|URL|режим доступа/i.test(t) ? 'web' : 'other', text: t });
    this.srcByNum.set(num ?? this.srcByNum.size + 1, id);
  }
  private textToSource(it: Extract<Item, { k: 'p' }>) {
    const m = it.text.match(/^\s*\[?(\d{1,3})[\].)]?[\s\u00a0]+/);
    this.addSource(it.text, m ? Number(m[1]) : undefined);
  }
  private listToSources(node: J) {
    for (const li of flattenList(node)) this.addSource(inlineText(li));
  }

  // --- ссылки на объекты и источники (после разбора всего фрагмента)
  private postRefs() {
    // ссылки Википедии [1] -> источники
    for (const [id, text] of this.dom.wikiRefs) {
      const sid = 's' + newId().slice(1);
      this.wikiSrc.set(id, sid);
      this.sources.push({ id: sid, kind: /https?:\/\//.test(text) ? 'web' : 'other', text });
    }
    const webSrc = new Map<string, string>();
    const visit = (arr: J[]) => {
      for (const n of arr) {
        if (!n.content) continue;
        if (['paragraph', 'caption'].includes(n.type!)) n.content = this.refsInline(n.content, webSrc);
        else visit(n.content);
      }
    };
    visit(this.out);
    visit(this.apps);
  }

  private refsInline(inl: J[], webSrc: Map<string, string>): J[] {
    const out: J[] = [];
    for (const n of inl) {
      if ((n as any).__wikiRef) {
        const sid = this.wikiSrc.get((n as any).__wikiRef);
        if (sid) { out.push({ type: 'cite', attrs: { items: [{ source: sid, loc: '' }] } }); this.stats.cites++; }
        continue;
      }
      if (n.type === 'footnote') {
        n.attrs = { ...n.attrs, content: this.refsInline(n.attrs?.content || [], webSrc) };
        out.push(n);
        continue;
      }
      if (n.type !== 'text') { out.push(n); continue; }
      const link = n.marks?.find((m) => m.type === 'link');
      if (link && this.o.links === 'sources' && /^https?:/i.test(link.attrs?.href || '')) {
        const href = link.attrs!.href;
        let sid = webSrc.get(href);
        if (!sid) {
          sid = 's' + newId().slice(1);
          webSrc.set(href, sid);
          this.sources.push({ id: sid, kind: 'web', title: (n.text || '').trim() || href, url: href, accessed: this.o.today });
        }
        out.push(...this.refsText({ ...n, marks: n.marks!.filter((m) => m.type !== 'link') }));
        out.push({ type: 'cite', attrs: { items: [{ source: sid, loc: '' }] } });
        this.stats.cites++;
        continue;
      }
      if (link && this.o.links === 'text') n.marks = n.marks!.filter((m) => m.type !== 'link');
      if (n.marks && !n.marks.length) delete n.marks;
      out.push(...this.refsText(n));
    }
    return out;
  }

  /** Текстовый узел -> узлы с xref/cite вместо номеров, которые есть во вставленном фрагменте. */
  private refsText(n: J): J[] {
    const t = n.text || '';
    const marks = n.marks;
    const repl: { from: number; to: number; node: J }[] = [];
    const add = (from: number, to: number, node: J) => { if (!repl.some((r) => from < r.to && to > r.from)) repl.push({ from, to, node }); };
    const numTok = new RegExp(NUM, 'g');
    const scan = (re: RegExp, map: Map<string, string>, grp: number, paren = false) => {
      for (const m of t.matchAll(re)) {
        const base = m.index! + m[0].length - m[grp].length;
        const body = m[grp];
        const tokRe = paren ? /\(\s*((?:[А-ЯA-Z]\.)?\d{1,3}(?:\.\d{1,3})?)\s*\)/g : numTok;
        for (const k of body.matchAll(tokRe)) {
          const key = paren ? k[1] : k[0];
          const id = map.get(key);
          if (!id || id === 'pending') continue;
          add(base + k.index!, base + k.index! + k[0].length, { type: 'xref', attrs: { target: id } });
        }
      }
    };
    const L = '(?<![А-Яа-яЁёA-Za-z])';
    const LIST = '(' + NUM + '(?:[\\s\\u00a0]*(?:[,–—-]|и|или)[\\s\\u00a0]*' + NUM + ')*)';
    scan(new RegExp(L + '(?:рисун(?:ок|ка|ку|ком|ке|ки|ков|кам|ками|ках)|рис\\.)[\\s\\u00a0]*' + LIST, 'gi'), this.maps.fig, 1);
    scan(new RegExp(L + '(?:таблиц(?:а|ы|е|у|ей|ею|ам|ами|ах)|табл\\.)[\\s\\u00a0]*' + LIST, 'gi'), this.maps.tbl, 1);
    scan(new RegExp(L + '(?:листинг(?:а|у|ом|е|и|ов|ам|ами|ах)?)[\\s\\u00a0]*' + LIST, 'gi'), this.maps.lst, 1);
    scan(new RegExp(L + '(?:раздел|подраздел|пункт|глав)[а-яё]*[\\s\\u00a0]+(\\d{1,2}(?:\\.\\d{1,2}){0,3})(?![\\d.]*\\d)', 'gi'), this.maps.sec, 1);
    scan(new RegExp(L + '(?:формул|выражени|соотношени|уравнени|равенств|зависимост)[а-яё]*[\\s\\u00a0]*' +
      '((?:\\(\\s*' + NUM + '\\s*\\)(?:[\\s\\u00a0]*(?:[,–—-]|и|или)[\\s\\u00a0]*)?)+)', 'gi'), this.maps.eq, 1, true);
    for (const m of t.matchAll(new RegExp(L + '(приложени(?:е|я|ю|ем|и|ях))[\\s\\u00a0]+([А-ЯA-Z])(?![А-Яа-яЁёA-Za-z])', 'g'))) {
      const id = this.maps.app.get(m[2]);
      if (id) add(m.index! + m[0].length - 1, m.index! + m[0].length, { type: 'xref', attrs: { target: id } });
    }
    // [3], [1, 3], [2–4], [5, с. 17]
    if (this.srcByNum.size) {
      for (const m of t.matchAll(/\[(\d{1,3}(?:[\s\u00a0]*[-–—,;][\s\u00a0]*\d{1,3})*)(?:[\s\u00a0]*,[\s\u00a0]*((?:с|c|p)\.[\s\u00a0]*[\d\s\u00a0–—-]+))?\]/g)) {
        const nums: number[] = [];
        for (const part of m[1].split(/[,;]/)) {
          const r = part.split(/[-–—]/).map((x) => Number(x.trim()));
          if (r.length === 2 && r[1] > r[0] && r[1] - r[0] < 50) for (let k = r[0]; k <= r[1]; k++) nums.push(k);
          else nums.push(r[0]);
        }
        if (!nums.every((k) => this.srcByNum.has(k))) continue;
        const loc = (m[2] || '').replace(/[\s\u00a0]+/g, ' ').trim();
        add(m.index!, m.index! + m[0].length, { type: 'cite', attrs: { items: nums.map((k, idx) => ({ source: this.srcByNum.get(k)!, loc: idx === nums.length - 1 ? loc : '' })) } });
      }
    }
    if (!repl.length) return [n];
    repl.sort((a, b) => a.from - b.from);
    const out: J[] = [];
    let pos = 0;
    for (const r of repl) {
      if (r.from > pos) out.push(marks ? { type: 'text', text: t.slice(pos, r.from), marks } : { type: 'text', text: t.slice(pos, r.from) });
      out.push(r.node);
      if (r.node.type === 'xref') this.stats.xrefs++; else this.stats.cites++;
      pos = r.to;
    }
    if (pos < t.length) out.push(marks ? { type: 'text', text: t.slice(pos), marks } : { type: 'text', text: t.slice(pos) });
    return out;
  }
}

// ------------------------------------------------------------------------------------------------ помощники
function nextIndex(items: Item[], i: number): number {
  let k = i + 1;
  while (k < items.length && items[k].k === 'p' && !(items[k] as any).text.trim() && !(items[k] as any).inl.length) k++;
  return k;
}
function nextText(items: Item[], i: number): string {
  const it = items[nextIndex(items, i)];
  return it && it.k === 'p' ? it.text : '';
}

function onlyMathWithNumber(inl: J[]): boolean {
  const maths = inl.filter((n) => n.type === 'mathInline');
  if (!maths.length) return false;
  const rest = inl.filter((n) => n.type !== 'mathInline').map((n) => (n.type === 'text' ? n.text : n.type === 'hardBreak' ? ' ' : 'x')).join('');
  if (!/^[\s\u00a0.,;:]*$/.test(rest) && !EQ_NUM_ONLY.test(rest.replace(/^[\s\u00a0.,;:]+/, ''))) return false;
  // обычная строчная формула в абзаце (одна короткая) тоже бывает отдельной строкой - считаем формулой,
  // если это выключная формула Word (oMathPara) или есть номер
  return maths.some((n) => (n as any).__math?.display) || /\(\s*\d/.test(rest) || maths.length >= 1 && !rest.trim();
}

function stripManualMarker(inl: J[]): J[] {
  const t = inlineText(inl);
  const m = MANUAL_MARK.exec(t);
  return m ? stripPrefix(inl, m[0].length) : inl;
}

function listMarkerText(marker: string): string {
  return DASH_MARK.test(marker) || !marker ? '–' : marker;
}

function splitWhere(inl: J[]): { sym: string; desc: string } | null {
  // «символ – расшифровка»: разделитель - первое тире после символа
  const parts: { t: string; math: boolean }[] = inl.map((n) => (n.type === 'mathInline' ? { t: latexOf(n), math: true } :
    { t: n.type === 'text' ? n.text || '' : n.type === 'hardBreak' ? ' ' : '', math: false }));
  let sym = '';
  let k = 0;
  let dashAt = -1;
  for (; k < parts.length; k++) {
    const p = parts[k];
    if (p.math) { sym += (sym ? ' ' : '') + p.t; continue; }
    const m = p.t.match(/^([^–—−]*?)[\s\u00a0]*[–—−-][\s\u00a0]+/);
    if (m && !(m[1].trim().length > 40)) {
      if (m[1].trim()) sym += (sym ? ' ' : '') + m[1].trim();
      dashAt = m[0].length;
      break;
    }
    sym += p.t;
    if (sym.length > 40) return null;
  }
  if (dashAt < 0 || !sym.trim()) return null;
  const rest = [{ t: parts[k].t.slice(dashAt), math: false }, ...parts.slice(k + 1)];
  const desc = rest.map((p) => (p.math ? `$${p.t}$` : p.t)).join('').replace(/[\s\u00a0]+/g, ' ').replace(/[;.,]\s*$/, '').trim();
  const s = sym.replace(/[\s\u00a0]+/g, ' ').replace(/,\s*$/, '').trim();
  return desc ? { sym: s, desc } : null;
}

/** 4.4: на первом уровне - дефис или «а)»; «1)» - только во вложенном перечислении. true - вид исправлен. */
function fixTopDigits(entries: ListEntry[]): boolean {
  const base = Math.min(...entries.map((e) => e.level));
  if (entries[0].kind !== 'digit' || entries.some((e) => e.kind === 'letter')) return false;
  for (const e of entries) if (e.level === base && e.kind === 'digit') e.kind = 'letter';
  return true;
}

function buildList(entries: ListEntry[]): J {
  const base = Math.min(...entries.map((e) => e.level));
  const root: J = { type: 'stoList', attrs: { kind: entries[0].kind }, content: [] };
  const stack: { level: number; list: J }[] = [{ level: 0, list: root }];
  for (const e of entries) {
    const lvl = Math.min(2, e.level - base);
    while (stack.length > 1 && lvl < stack[stack.length - 1].level) stack.pop();
    let top = stack[stack.length - 1];
    if (lvl > top.level && top.list.content!.length) {
      const parent = top.list.content![top.list.content!.length - 1];
      let sub = parent.content![1];
      if (!sub) { sub = { type: 'stoList', attrs: { kind: e.kind }, content: [] }; parent.content!.push(sub); }
      stack.push({ level: lvl, list: sub });
      top = stack[stack.length - 1];
    }
    top.list.content!.push({ type: 'stoListItem', content: [para(e.inl)] });
  }
  return root;
}

/** Список из HTML (ul/ol) -> гарантированно корректный stoList (глубина <= 3, в пункте - абзац). */
export function normalizeListJson(node: J, depth = 0): J {
  const items = (node.content || []).map((li) => {
    const p = li.content?.[0]?.type === 'paragraph' ? li.content[0] : { type: 'paragraph' };
    const sub = li.content?.[1];
    const out: J = { type: 'stoListItem', content: [p] };
    if (sub?.type === 'stoList' && sub.content?.length) {
      if (depth >= 2) {
        // глубже трёх уровней - поднимаем пункты на третий уровень
        return [out, ...normalizeListJson(sub, depth).content!];
      }
      out.content!.push(normalizeListJson(sub, depth + 1));
    }
    return [out];
  }).flat();
  return { type: 'stoList', attrs: { kind: node.attrs?.kind || 'auto' }, content: items };
}

function flattenList(node: J, out: J[][] = [], marker = '–'): J[][] {
  const kind = node.attrs?.kind;
  (node.content || []).forEach((li, k) => {
    const m = kind === 'digit' ? `${k + 1})` : kind === 'letter' ? `${'абвгдежиклмнпрстуфхцшщэюя'[k] || 'я'})` : marker;
    const p = li.content?.[0];
    out.push([textNode(m + ' ', {}), ...(p?.content || [])]);
    if (li.content?.[1]) flattenList(li.content[1], out);
  });
  return out;
}

function directRows(el: HTMLTableElement): HTMLTableRowElement[] {
  const rows: HTMLTableRowElement[] = [];
  for (const ch of Array.from(el.children)) {
    if (ch.tagName === 'TR') rows.push(ch as HTMLTableRowElement);
    else if (['THEAD', 'TBODY', 'TFOOT'].includes(ch.tagName)) for (const r of Array.from(ch.children)) if (r.tagName === 'TR') rows.push(r as HTMLTableRowElement);
  }
  // пустые служебные строки Word (height=0) и строки без ячеек
  return rows.filter((r) => directCells(r).length > 0);
}
function directCells(tr: Element): Element[] {
  return Array.from(tr.children).filter((c) => (c.tagName === 'TD' || c.tagName === 'TH') &&
    !/mso-cell-special\s*:\s*placeholder/i.test(c.getAttribute('style') || ''));
}
function cssAlign(c: Element): string {
  const p = c.querySelector('p[align], p[style*="text-align"]');
  const st = (c.getAttribute('style') || '') + ';' + (p?.getAttribute('style') || '');
  return (st.match(/text-align\s*:\s*(left|center|right)/i) || [])[1] || p?.getAttribute('align') || '';
}
function colWidths(rows: HTMLTableRowElement[], cells: Element[][]): number[] | null {
  // ширины граф - по строке без объединений (атрибут width или style width в pt/px)
  for (let r = rows.length - 1; r >= 0; r--) {
    const cs = cells[r];
    if (cs.length < 2 || cs.some((c) => Number(c.getAttribute('colspan') || 1) > 1)) continue;
    const w = cs.map((c) => {
      const a = Number(c.getAttribute('width'));
      if (a > 0) return a;
      const m = (c.getAttribute('style') || '').match(/(?:^|;)\s*width\s*:\s*([\d.]+)\s*(pt|px|cm)?/i);
      return m ? Number(m[1]) * (m[2] === 'cm' ? 37.8 : m[2] === 'pt' ? 1.333 : 1) : 0;
    });
    if (w.some((x) => !x)) return null;
    const sum = w.reduce((a, b) => a + b, 0);
    const pct = w.map((x) => Math.round((x / sum) * 1000) / 10);
    // почти равные графы - ширины не нужны (writer подберёт по содержимому)
    if (Math.max(...pct) - Math.min(...pct) < 5) return null;
    return pct;
  }
  return null;
}
function rowText(r: J): string {
  const walk = (n: J): string => (n.type === 'text' ? n.text || '' : (n.content || []).map(walk).join(' '));
  return walk(r).replace(/\s+/g, ' ').trim();
}
